/**
 * How alive the valley is, from the balance. Ported from ecology.js.
 *
 * `life` runs 0..1 and is what brings residents in -- each has a threshold
 * in layout.ts. Barren is exactly zero, so nobody at all lives in a valley
 * with nothing saved. Anything positive starts at 0.34, which is enough for
 * the first few residents to appear the moment a family saves a single
 * dollar: that first dollar is the one that most needs a visible reward.
 *
 * THIS USES THE DISPLAY'S FIXED $100, not the family's `gardenTarget`, and
 * that is a known inconsistency rather than an oversight. The painting stage
 * does follow the target (see scene.ts). The residents follow this. Moving
 * them onto the target is a one-line change here, and it is the display's
 * behaviour to change first -- a family looking at the kitchen screen and a
 * phone should see the same number of deer.
 */

export const LIFE_TARGET = 10_000;

export type Level = 'barren' | 'normal' | 'plentiful';

export interface Ecology {
  level: Level;
  life: number;
}

export function describe(cents: number): Ecology {
  if (cents <= 0) return { level: 'barren', life: 0 };
  if (cents < LIFE_TARGET) return { level: 'normal', life: 0.34 + (cents / LIFE_TARGET) * 0.35 };
  return { level: 'plentiful', life: 1 };
}

/**
 * Whether one resident is out, 0 or 1. Out of season is never; otherwise a
 * resident is simply there once the valley's health passes its threshold.
 *
 * CHANGED 2026-10-12: residents used to fade in over the 1/12 of health just
 * past their threshold, which left half-transparent animals on screen for
 * any balance in that band. They now appear whole the moment the threshold
 * is met. The display's scene.js follows the same rule.
 */
export function presence(life: number, threshold: number, active: boolean): number {
  return active && life > threshold ? 1 : 0;
}
